/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import * as React from 'react';
import {ViewStyle} from 'react-native';

import {TabView, SceneMap, TabBar} from 'react-native-tab-view';

type routesProps = {
  key: number;
  title: string;
  icon?: string;
  accessibilityLabel?: string;
  testID?: number;
};

type TabBarPosition = 'top' | 'bottom';

type KeyboardDismissMode = 'auto' | 'on-drag' | 'none';

type SpringConfigProps = {
  damping: number;
  mass: number;
  stiffness: number;
  restSpeedThreshold: number;
  restDisplacementThreshold: number;
};

type GestureHandlerProps = {
  maxPointers: number;
  waitFor: any;
};

interface TabViewVProps {
  navigationState: any;
  onIndexChange(index: number): void;
  renderScene(route: routesProps): any;
  initialLayout?: {width?: number; height?: number};
  tabBarPosition?: TabBarPosition;
  lazy?: boolean;
  lazyPreloadDistance?: number;
  renderLazyPlaceholder?(): {};
  keyboardDismissMode?: KeyboardDismissMode;
  swipeEnabled?: boolean;
  swipeVelocityImpact?: number;
  onSwipeStart?(): {};
  onSwipeEnd?(): {};
  timingConfig?: {duration: number};
  springConfig?: SpringConfigProps;
  springVelocityScale?: number;
  position?: any;
  sceneContainerStyle?: ViewStyle;
  style?: ViewStyle;
  gestureHandlerProps?: GestureHandlerProps;

  getLabelText?(route: routesProps): {};
  getAccessible?(route: routesProps): {};
  getAccessibilityLabel?(route: routesProps): {};
  getTestID?(route: routesProps): {};
  renderIcon?(route: routesProps, focused: any, color: string): {};
  renderLabel?(route: routesProps, focused: any, color: string): {};
  renderIndicator?(route: routesProps): {};
  renderBadge?(route: routesProps): {};
  onTabPress?(route: routesProps, preventDefault: any): {};
  onTabLongPress?(): {};
  activeColor?: string;
  inactiveColor?: string;
  pressColor?: string;
  pressOpacity?: number;
  scrollEnabled?: boolean;
  bounces?: boolean;
  tabStyle?: ViewStyle;
  indicatorStyle?: ViewStyle;
  indicatorContainerStyle?: ViewStyle;
  labelStyle?: ViewStyle;
  contentContainerStyle?: ViewStyle;
  tabBarStyle?: ViewStyle;
  
  
  ovescroll?: boolean;
}

export const TabViewV = (props: TabViewVProps): JSX.Element => {
  const _renderScene = ({route}) => {
    const {renderScene} = props;
    return renderScene(route);
  };

  const _renderTabBar = (tabBarProps: any): React.ReactNode => {
    const {
      getLabelText,
      getAccessible,
      getAccessibilityLabel,
      getTestID,
      renderIcon,
      renderLabel,
      renderIndicator,
      renderBadge,
      onTabPress,
      onTabLongPress,
      activeColor,
      inactiveColor,
      pressColor,
      pressOpacity,
      scrollEnabled,
      bounces,
      tabStyle,
      indicatorStyle,
      indicatorContainerStyle,
      labelStyle,
      contentContainerStyle,
      tabBarStyle,
      titles,
    } = props;
    return (
      <TabBar
        {...tabBarProps}
        getLabelText={getLabelText}
        getAccessible={getAccessible}
        getAccessibilityLabel={getAccessibilityLabel}
        getTestID={getTestID}
        renderIcon={renderIcon}
        renderLabel={renderLabel}
        renderIndicator={renderIndicator}
        renderBadge={renderBadge}
        onTabPress={onTabPress}
        onTabLongPress={onTabLongPress}
        activeColor={activeColor}
        inactiveColor={inactiveColor}
        pressColor={pressColor}
        pressOpacity={pressOpacity}
        scrollEnabled={scrollEnabled}
        bounces={bounces}
        tabStyle={tabStyle}
        indicatorStyle={indicatorStyle}
        indicatorContainerStyle={indicatorContainerStyle}
        labelStyle={labelStyle}
        contentContainerStyle={contentContainerStyle}
        style={tabBarStyle}
      />
    );
  };

  // const _renderPager = (pagerProps: any) => {
  //   const {ovescroll} = props;
  //   return <ScrollPager {...pagerProps} ovescroll={ovescroll} />;
  // };

  const {
    navigationState,
    onIndexChange,
    initialLayout,
    tabBarPosition,
    lazy,
    lazyPreloadDistance,
    renderLazyPlaceholder,
    keyboardDismissMode,
    swipeEnabled,
    swipeVelocityImpact,
    onSwipeStart,
    onSwipeEnd,
    timingConfig,
    springConfig,
    springVelocityScale,
    position,
    sceneContainerStyle,
    style,
    gestureHandlerProps,
  } = props;
  return (
    <TabView
      navigationState={navigationState}
      onIndexChange={onIndexChange}
      renderScene={_renderScene}      
      initialLayout={initialLayout}
      tabBarPosition={tabBarPosition}
      renderTabBar={tabBarProps => _renderTabBar(tabBarProps)}
      // renderPager={(pagerProps) => _renderPager(pagerProps)}
      lazy={lazy}
      lazyPreloadDistance={lazyPreloadDistance}
      renderLazyPlaceholder={renderLazyPlaceholder}
      keyboardDismissMode={keyboardDismissMode}
      swipeEnabled={swipeEnabled}
      swipeVelocityImpact={swipeVelocityImpact}
      onSwipeStart={onSwipeStart}
      onSwipeEnd={onSwipeEnd}
      timingConfig={timingConfig}
      springConfig={springConfig}
      springVelocityScale={springVelocityScale}
      position={position}
      sceneContainerStyle={sceneContainerStyle}
      style={style}
      gestureHandlerProps={gestureHandlerProps}
    />
  );
};

TabViewV.defaultProps = {} as Partial<TabViewVProps>;
